/**
 * Wires the custom quantity-switcher UI to the SectionHeroes
 * sh-quantity-upsell app block.
 *
 * Responsibilities:
 *   1. Find the sh-quantity-upsell element that pairs with each custom
 *      switcher and wait for the app to mount its option buttons.
 *   2. Mirror quantity (`options[n].quantity`) and discount labels from the
 *      app config into card labels and "-N%" pills.
 *   3. Reflect the currently selected option as aria-checked on the
 *      matching custom card.
 *   4. Proxy clicks from the custom cards into the app's real option
 *      buttons so quantity selection still flows through the app's
 *      state machine and updates `properties[_sh-qu]`.
 *
 * The app block remains in the DOM; only its visible UI is hidden via
 * CSS (see assets/sh-quantity-switcher.css).
 */
(() => {
  const OPTION_SELECTORS = [
    '[role="radio"]',
    '[aria-checked]',
    '.sh-DetailQuantities_Options > *',
    '.sh-DetailQuantities_Options button',
    'input[type="radio"]'
  ];

  /**
   * Find the matching app block element for a given custom switcher.
   * Looks within the same wrapper that the snippet was rendered into.
   */
  const findAppBlock = (switcher) => {
    const host = switcher.closest('.sh-quantity-upsell-host') || switcher.parentElement;
    if (!host) return null;
    return host.querySelector('sh-quantity-upsell');
  };

  /**
   * Find the app block's option buttons. Tries selectors in order of
   * specificity and returns the first set with at least two entries.
   */
  const findAppOptions = (appBlock) => {
    if (!appBlock) return [];

    for (const selector of OPTION_SELECTORS) {
      const candidates = Array.from(appBlock.querySelectorAll(selector));
      const filtered = candidates.filter((el) => {
        // Skip nested matches that contain other candidates of the same kind.
        return !candidates.some((other) => other !== el && el.contains(other));
      });
      if (filtered.length >= 2) return filtered;
    }
    return [];
  };

  const parseDiscountFromText = (text) => {
    if (!text) return '';
    const match = text.match(/(\d+(?:[.,]\d+)?)\s*%/);
    if (!match) return '';
    return `-${match[1]}%`;
  };

  /**
   * Read the discount label rendered by the app for a given option.
   */
  const readOptionDiscount = (optionEl) => {
    if (!optionEl) return '';

    const discountText =
      optionEl.querySelector('[data-discount-text], [class*="discount"], [class*="Save"], [class*="save"]')?.textContent
      || optionEl.textContent
      || '';
    return parseDiscountFromText(discountText);
  };

  /**
   * Quantity from app config (`options[n].quantity`), same source as sh-price.liquid.
   * Falls back to parsing the button label, then card index + 1.
   */
  const readOptionQuantity = (appBlock, index, optionEl) => {
    const opt = appBlock?.options?.[index];
    if (opt != null && opt.quantity != null) {
      const qty = Number(opt.quantity);
      if (Number.isFinite(qty) && qty > 0) return qty;
    }

    if (optionEl) {
      const labelText = (optionEl.textContent || '').trim();
      const leadingNumber = labelText.match(/^(\d+)/);
      if (leadingNumber) {
        const parsed = Number.parseInt(leadingNumber[1], 10);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
      }
    }

    return index + 1;
  };

  // ── Unit-price helpers ──────────────────────────────────────────────────────

  const _productJsonCache = new Map();

  const loadProductJson = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (_productJsonCache.has(handle)) return Promise.resolve(_productJsonCache.get(handle));
    const root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
    return fetch(`${root}/products/${handle}.js`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { _productJsonCache.set(handle, j); return j; })
      .catch(() => { _productJsonCache.set(handle, null); return null; });
  };

  const findProductForm = (el) =>
    el.closest('form[action*="/cart/add"]:not(.installment)')
    || (el.closest('[id^="shopify-section"]') || document).querySelector(
        'form[action*="/cart/add"][data-type="add-to-cart-form"]'
      )
    || document.querySelector('form[action*="/cart/add"]:not(.installment)');

  const getCurrentVariantId = (el) => {
    const form = findProductForm(el);
    if (form) {
      const idInput = form.querySelector('[name="id"]');
      if (idInput && idInput.value) return parseInt(idInput.value, 10);
    }
    const urlId = new URLSearchParams(window.location.search).get('variant');
    return urlId ? parseInt(urlId, 10) : null;
  };

  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    const t = thousands !== undefined ? thousands : ',';
    const d = decimal !== undefined ? decimal : '.';
    const fixed = (Math.abs(number / 100)).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
    return precision > 0 ? parts.join(d) : parts[0];
  };

  const formatMoney = (cents, fmt) => {
    const f = fmt || '{{amount}}';
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (m, p) => {
      switch (p) {
        case 'amount': return formatWithDelimiters(cents, 2);
        case 'amount_no_decimals': return formatWithDelimiters(cents, 0);
        case 'amount_with_comma_separator': return formatWithDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatWithDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatWithDelimiters(cents, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return formatWithDelimiters(cents, 0, ' ');
        case 'amount_with_space_separator': return formatWithDelimiters(cents, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return formatWithDelimiters(cents, 2, ' ', '.');
        default: return m;
      }
    });
  };

  const computeUnitPriceCents = (variantPriceCents, opt) => {
    const qty = (opt && opt.quantity) ? Number(opt.quantity) : 1;
    const d = (opt && opt.discount) ? opt.discount : {};
    const dValue = parseFloat(d.value) || 0;
    let totalCents = variantPriceCents * qty;
    if (d.type === 'percentage' && dValue > 0) {
      totalCents = Math.max(0, Math.round(totalCents * (1 - dValue / 100)));
    } else if (d.type === 'fixed_amount' && dValue > 0) {
      totalCents = Math.max(0, totalCents - Math.round(dValue * 100));
    }
    return Math.round(totalCents / qty);
  };

  const revealLabel = (label) => {
    if (label) label.setAttribute('data-sh-price-ready', '');
  };

  const syncUnitPrices = (switcher, appBlock, options, productJson) => {
    const variantId = getCurrentVariantId(appBlock);
    const variants = productJson && productJson.variants;
    if (!variants) return;
    const variant = variantId
      ? variants.find((v) => v.id === variantId)
      : variants[0];
    if (!variant) return;

    const moneyFmt =
      (appBlock && appBlock.moneyFormat)
      || (window.Shopify && window.Shopify.moneyFormat)
      || '{{amount_with_comma_separator}} \u20ac';

    const cards = Array.from(switcher.querySelectorAll('[data-sh-quantity-card]'));
    cards.forEach((card) => {
      const index = Number.parseInt(card.dataset.quantityIndex, 10);
      const label = card.querySelector('.sh-quantity-switcher__label');
      if (!label) return;

      // Metafield override (custom.sh_quantity_texts) takes priority over auto-calc
      const override = (card.dataset.shLabelOverride || '').trim();
      if (override) {
        label.textContent = override;
        revealLabel(label);
        return;
      }

      // Auto-calculate discounted unit price
      const opt = appBlock && appBlock.options && appBlock.options[index];
      const unitCents = computeUnitPriceCents(variant.price, opt);
      label.textContent = `${formatMoney(unitCents, moneyFmt)} / St\u00fcck`;
      revealLabel(label);
    });
  };

  // ────────────────────────────────────────────────────────────────────────────

  const appOptionsReady = (appBlock, domOptions) => {
    if (!domOptions.length) return false;
    if (appBlock?.options?.length >= domOptions.length) return true;
    return domOptions.length >= 2;
  };

  /**
   * Determine whether the option is currently selected in the app block.
   */
  const isOptionSelected = (optionEl) => {
    if (!optionEl) return false;
    if (optionEl.matches('input[type="radio"], input[type="checkbox"]')) {
      return Boolean(optionEl.checked);
    }
    if (optionEl.getAttribute('aria-checked') === 'true') return true;
    if (optionEl.classList && (optionEl.classList.contains('selected') || optionEl.classList.contains('is-selected'))) {
      return true;
    }
    return false;
  };

  /**
   * Reflect the app block's selection state in the custom cards.
   */
  const setSelectedIndex = (switcher, selectedIndex) => {
    const cards = Array.from(switcher.querySelectorAll('[data-sh-quantity-card]'));

    cards.forEach((card) => {
      const index = Number.parseInt(card.dataset.quantityIndex, 10);
      const selected = index === selectedIndex;
      card.setAttribute('aria-checked', selected ? 'true' : 'false');
    });
  };

  const syncSelection = (switcher, options) => {
    let selectedIndex = options.findIndex(isOptionSelected);
    if (selectedIndex < 0) {
      const currentCard = switcher.querySelector('[data-sh-quantity-card][aria-checked="true"]');
      selectedIndex = currentCard ? Number.parseInt(currentCard.dataset.quantityIndex, 10) : 0;
    }

    setSelectedIndex(switcher, Number.isFinite(selectedIndex) ? selectedIndex : 0);
  };

  /**
   * Populate the discount pills under each custom card.
   */
  const syncDiscounts = (switcher, options) => {
    const cards = Array.from(switcher.querySelectorAll('[data-sh-quantity-card]'));
    cards.forEach((card) => {
      const index = Number.parseInt(card.dataset.quantityIndex, 10);
      const optionEl = options[index];
      const discountEl = card.querySelector('[data-sh-quantity-discount]');
      if (!discountEl) return;

      const discountLabel = readOptionDiscount(optionEl);
      if (discountLabel) {
        discountEl.textContent = discountLabel;
        discountEl.hidden = false;
      } else {
        discountEl.textContent = '';
        discountEl.hidden = true;
      }
    });
  };

  /**
   * Proxy a click from a custom card onto the matching app option button.
   * Programmatic click on a `display:none` ancestor still fires listeners,
   * which is what we rely on to keep the app's state machine intact.
   */
  const dispatchToApp = (optionEl) => {
    if (!optionEl) return;

    if (optionEl.matches('input[type="radio"], input[type="checkbox"]')) {
      if (!optionEl.checked) {
        optionEl.checked = true;
        optionEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
      optionEl.click();
      return;
    }

    optionEl.click();
  };

  const updateCardMeta = (switcher, card, index, appBlock, domOptions) => {
    const cardIndex = index + 1;
    const quantity = readOptionQuantity(appBlock, index, domOptions[index]);
    const badgeText = switcher.dataset.badgeText || '';
    const badgePosition = Number.parseInt(switcher.dataset.badgePosition, 10);

    card.dataset.quantityIndex = String(index);
    card.dataset.quantityValue = String(quantity);

    const label = card.querySelector('.sh-quantity-switcher__label');
    if (label) label.textContent = `${quantity}x`;

    const shouldShowRibbon = badgeText && Number.isFinite(badgePosition) && badgePosition === cardIndex;
    const ribbon = card.querySelector('[data-sh-quantity-ribbon], .sh-quantity-switcher__ribbon');
    if (ribbon) {
      if (shouldShowRibbon) {
        ribbon.textContent = badgeText;
        ribbon.hidden = false;
        card.classList.add('sh-quantity-switcher__card--has-ribbon');
      } else if (ribbon.hasAttribute('data-sh-quantity-ribbon')) {
        ribbon.textContent = '';
        ribbon.hidden = true;
        card.classList.remove('sh-quantity-switcher__card--has-ribbon');
      }
    }
  };

  /**
   * The app block is the source of truth for quantity option count.
   * Metafield images are used for the cards that exist from Liquid.
   * Missing cards are appended with the product featured image fallback.
   */
  const normalizeCardCount = (switcher, appBlock, options) => {
    const optionCount = options.length;
    if (!optionCount) return;

    let cards = Array.from(switcher.querySelectorAll('[data-sh-quantity-card]'));

    while (cards.length > optionCount) {
      const card = cards.pop();
      if (card) card.remove();
    }

    const template = switcher.querySelector('template[data-sh-quantity-fallback-card]');
    while (cards.length < optionCount && template) {
      const clone = template.content.firstElementChild?.cloneNode(true);
      if (!clone) break;
      switcher.insertBefore(clone, template);
      cards.push(clone);
    }

    cards.forEach((card, index) => updateCardMeta(switcher, card, index, appBlock, options));
  };

  /**
   * Wire up a single custom switcher to its sibling app block.
   * Waits (briefly) for the app to render options before binding.
   */
  const initSwitcher = (switcher) => {
    if (switcher.dataset.shQuantitySwitcherReady === 'true') return;

    const appBlock = findAppBlock(switcher);
    if (!appBlock) return;

    // Pre-warm the product JSON cache immediately — runs in parallel with the
    // app-options polling loop so the fetch is usually done by the time we need it.
    const productHandle = appBlock.dataset && appBlock.dataset.productHandle;
    const productJsonPromise = productHandle
      ? loadProductJson(productHandle)
      : Promise.resolve(null);

    let attempts = 0;
    const maxAttempts = 40; // ~4 seconds at 100ms intervals

    const attemptBind = () => {
      const options = findAppOptions(appBlock);
      if (!appOptionsReady(appBlock, options)) {
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(attemptBind, 100);
        }
        return;
      }

      switcher.dataset.shQuantitySwitcherReady = 'true';
      normalizeCardCount(switcher, appBlock, options);

      syncDiscounts(switcher, options);
      syncSelection(switcher, options);

      if (!options.some(isOptionSelected) && options[0]) {
        dispatchToApp(options[0]);
        syncSelection(switcher, options);
      }

      const syncPrices = () => {
        productJsonPromise.then((pj) => {
          if (pj) {
            syncUnitPrices(switcher, appBlock, options, pj);
          } else {
            // No product JSON — fall back to showing the Nx labels
            switcher.querySelectorAll('.sh-quantity-switcher__label').forEach(revealLabel);
          }
        });
      };

      // Fallback: reveal whatever text is in the labels after 3 s so they
      // never stay permanently hidden if both fetches are slow or fail.
      setTimeout(() => {
        switcher.querySelectorAll('.sh-quantity-switcher__label').forEach(revealLabel);
      }, 3000);

      syncPrices();

      const refreshCardMeta = () => {
        const cards = Array.from(switcher.querySelectorAll('[data-sh-quantity-card]'));
        cards.forEach((card, index) => updateCardMeta(switcher, card, index, appBlock, options));
        syncPrices();
      };

      appBlock.addEventListener('sectionheroes:qu:changed', refreshCardMeta);

      switcher.addEventListener('click', (event) => {
        const card = event.target.closest('[data-sh-quantity-card]');
        if (!card) return;
        event.preventDefault();

        const index = Number.parseInt(card.dataset.quantityIndex, 10);
        const optionEl = options[index];
        if (!optionEl) return;

        setSelectedIndex(switcher, index);
        dispatchToApp(optionEl);
        setTimeout(() => syncSelection(switcher, options), 50);
      });

      // Watch the app's options for selection changes triggered by
      // anything else (theme editor preview, app's own JS, etc.) and
      // keep our cards in sync.
      const observer = new MutationObserver(() => {
        refreshCardMeta();
        syncSelection(switcher, options);
        syncDiscounts(switcher, options);
      });

      options.forEach((option) => {
        observer.observe(option, {
          attributes: true,
          attributeFilter: ['aria-checked', 'class', 'checked']
        });
      });

      const productForm = findProductForm(appBlock);
      if (productForm) {
        productForm.addEventListener('change', (e) => {
          const t = e.target;
          if (!t) return;
          const isVariantInput =
            t.name === 'id'
            || (t.name && t.name.indexOf('options[') === 0)
            || (t.matches && t.matches('[data-option-position], [data-option-name]'));
          if (isVariantInput) setTimeout(syncPrices, 50);
        });
      }
    };

    attemptBind();
  };

  const initAll = () => {
    document.querySelectorAll('[data-sh-quantity-switcher]').forEach(initSwitcher);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-run init when the theme editor swaps sections in/out so the
  // switcher stays wired during live editing.
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);
})();
